import React, { ReactElement, useCallback, useMemo, useContext, ComponentProps } from 'react';
import { FlashList } from '@shopify/flash-list';
import { ActivityIndicator, FlatList, StyleProp, StyleSheet, View, ViewStyle, ViewToken } from 'react-native';
import useVisualRefetchState from '_utils/hooks/useVisualRefetchState';
import { isWeb } from '_utils/isPlatform';
import { ThemeContext } from '_utils/themeContext';
import Loader from 'Components/Loader';

interface ItemWithId {
    id: number | string;
}

interface FlashListProps<ItemType extends ItemWithId>
    extends Pick<
        ComponentProps<typeof FlashList<ItemType>>,
        'contentContainerStyle' | 'renderItem' | 'estimatedItemSize'
    > {
    /**
     * Whether to use FlashList instead of FlatList
     * FlashList is faster, but it has its own quirks
     */
    flashlist: true;
}

interface FlatListProps<ItemType extends ItemWithId>
    extends Pick<ComponentProps<typeof FlatList<ItemType>>, 'renderItem' | 'numColumns' | 'contentContainerStyle'> {
    /**
     * Whether to use FlashList instead of FlatList
     * FlashList is faster, but it has its own quirks
     */
    flashlist?: false | undefined;
    style?: StyleProp<ViewStyle>;
}

export type QueryViewProps<ItemType extends ItemWithId> = {
    onViewableItemsChanged?:
        | ((info: { viewableItems: Array<ViewToken>; changed: Array<ViewToken> }) => void)
        | null
        | undefined;
    viewabilityConfig?: { viewAreaCoveragePercentThreshold: number };
    data: ItemType[];
    loadMore?: () => void;
    isLoadingMore?: boolean;
    onRefresh?: () => void;
    isRefreshing?: boolean;
    emptyList?: ReactElement;
    top?: ReactElement;
    stickyTop?: boolean;
    /**
     * Whether to persist the handled keyboard taps
     */
    keyboardShouldPersistTaps?: boolean;
    isSearching?: boolean;
    columnWrapperStyle?: StyleProp<ViewStyle>;
    footer?: ReactElement;
} & (FlashListProps<ItemType> | FlatListProps<ItemType>);

const QueryView = <ItemType extends ItemWithId>({
    onViewableItemsChanged,
    viewabilityConfig,
    data,
    isLoadingMore,
    loadMore,
    isRefreshing,
    onRefresh,
    emptyList,
    top,
    stickyTop,
    keyboardShouldPersistTaps,
    isSearching,
    columnWrapperStyle,
    footer,
    ...props
}: QueryViewProps<ItemType>): ReactElement => {
    const { theme } = useContext(ThemeContext);
    const [refreshing, handleRefresh] = useVisualRefetchState(isRefreshing ?? false, onRefresh ?? (() => {}));
    const keyExtractor = useCallback((item: ItemType, index: number) => `${item.id ?? index}`, []);

    const footerComponent = useMemo(() => {
        if (isLoadingMore) {
            return <ActivityIndicator color={theme.black} />;
        } else {
            return footer ?? <View />;
        }
    }, [footer, isLoadingMore, theme.black]);

    const handleOnEndReached = loadMore ? () => loadMore() : undefined;

    const commonProps = {
        keyboardShouldPersistTaps: keyboardShouldPersistTaps ? 'handled' : undefined,
        onViewableItemsChanged: onViewableItemsChanged,
        viewabilityConfig: viewabilityConfig,
        data: isSearching ? [] : data,
        showsVerticalScrollIndicator: isWeb(),
        keyExtractor: keyExtractor,
        initialNumToRender: 5,
        onEndReached: handleOnEndReached,
        ListFooterComponent: footerComponent,
        onEndReachedThreshold: 0.5,
        onRefresh: onRefresh ? handleRefresh : undefined,
        refreshing: refreshing,

        ListEmptyComponent: isSearching ? (
            <View style={styles.fullHeight}>
                <Loader />
            </View>
        ) : (
            emptyList
        ),
        ListHeaderComponent: top,
        stickyHeaderIndices: stickyTop ? [0] : undefined,
    } as const;

    if (props.flashlist) {
        return <FlashList {...commonProps} {...props} />;
    } else {
        return (
            <FlatList
                {...commonProps}
                columnWrapperStyle={
                    props.numColumns && props.numColumns > 1
                        ? (columnWrapperStyle ?? styles.columnWrapperStyle)
                        : undefined
                }
                numColumns={props.numColumns}
                {...props}
            />
        );
    }
};

const styles = StyleSheet.create({
    main: {
        flex: 1,
    },
    fullHeight: {
        height: '100%',
    },
    columnWrapperStyle: { flex: 1, justifyContent: 'flex-start' },
});

export default QueryView;
